import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import {
    IndiciaireEnum,
    MantyStatus,
    RecurrenceEnum,
    RetirementSystem,
} from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { HrPositionMapping } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/type';
import {
    INDICIAIRE,
    MANTY_STATUS,
    RECURRENCE,
    RETIREMENT_SYSTEM,
} from 'routes/HumanResourcesModule/business/definitions';
import { RQAPICall } from 'store/APIClient';
import { dispatch } from 'store/store';
import { resetGVTDataQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/GVTData/helpers/hooks/gvtData';
import { resetEnvelopeQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/helpers/apiHelpers';

const POSITION_MAPPING_API_KEY = 'position_mapping';

/**
 * Hook to retrieve position mappings
 */
export const useGetHrPositionMappings = (): UseQueryResult<
    HrPositionMapping[]
> => {
    const endpoint = 'human_resources/position_mapping';

    return useQuery({
        queryKey: [POSITION_MAPPING_API_KEY],
        queryFn: async () => {
            const getResponse = await RQAPICall({
                endpoint,
            });
            return getResponse.content.data;
        },
        meta: {
            useGlobalOnError: true,
        },
    });
};

export type PutPositionMappingPayload = {
    hrPositionMappingIds: number[];
    [RECURRENCE]?: RecurrenceEnum;
    [MANTY_STATUS]?: MantyStatus;
    [RETIREMENT_SYSTEM]?: RetirementSystem;
    [INDICIAIRE]?: IndiciaireEnum;
};

/**
 * Hook to update HR position mappings
 */
export const usePutPositionMapping = (shouldResetQuery?: boolean) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (
            payload: PutPositionMappingPayload,
        ): Promise<{
            content: { data: HrPositionMapping[]; message: string };
        }> =>
            RQAPICall({
                endpoint: 'human_resources/position_mapping',
                method: 'PUT',
                body: payload,
            }),

        onSuccess: ({ content }) => {
            queryClient.setQueryData(
                [POSITION_MAPPING_API_KEY],
                (oldPosition: HrPositionMapping[]) =>
                    oldPosition.map((oldP) => {
                        const updatedPosition = content.data.find(
                            (p: HrPositionMapping) => p.id === oldP.id,
                        );
                        return updatedPosition || oldP;
                    }),
            );
            if (shouldResetQuery) {
                queryClient.resetQueries({
                    queryKey: [POSITION_MAPPING_API_KEY],
                });
            }
            resetGVTDataQueries(queryClient);
            resetEnvelopeQueries(queryClient);
            dispatch.vision.notifyUser({
                message: content.message,
                snackType: 'success',
            });
        },
        meta: { useGlobalOnError: true },
    });
};
